/**
 * Composable for formatting prices with proper currency symbols
 * Removes .00 from whole numbers and places currency symbol before the amount
 */

export const usePriceFormatting = () => {
  // Get currency symbol from currency code
  const getCurrencySymbol = (currencyCode?: string) => {
    if (!currencyCode) return "$";

    const currencyMap: Record<string, string> = {
      USD: "$",
      GBP: "£",
      EUR: "€",
      CAD: "C$",
      AUD: "A$",
      JPY: "¥",
      CHF: "CHF",
      SEK: "kr",
      NOK: "kr",
      DKK: "kr",
      PLN: "zł",
      CZK: "Kč",
      HUF: "Ft",
      RUB: "₽",
      BRL: "R$",
      MXN: "$",
      INR: "₹",
      CNY: "¥",
      KRW: "₩",
      SGD: "S$",
      HKD: "HK$",
      NZD: "NZ$",
      ZAR: "R",
      TRY: "₺",
      ILS: "₪",
      AED: "د.إ",
      SAR: "﷼",
      QAR: "﷼",
      KWD: "د.ك",
      BHD: "د.ب",
      OMR: "﷼",
      JOD: "د.ا",
      LBP: "ل.ل",
      EGP: "£",
      MAD: "د.م.",
      TND: "د.ت",
      DZD: "د.ج",
      LYD: "ل.د",
      SDG: "ج.س.",
      ETB: "Br",
      KES: "KSh",
      UGX: "USh",
      TZS: "TSh",
      ZMW: "ZK",
      BWP: "P",
      NAD: "N$",
      SZL: "L",
      LSL: "L",
      MUR: "₨",
      SCR: "₨",
      MVR: "ރ",
      PKR: "₨",
      BDT: "৳",
      LKR: "₨",
      NPR: "₨",
      AFN: "؋",
      MMK: "K",
      LAK: "₭",
      KHR: "៛",
      VND: "₫",
      THB: "฿",
      MYR: "RM",
      IDR: "Rp",
      PHP: "₱",
      TWD: "NT$",
      MOP: "MOP$",
      BND: "B$",
      FJD: "FJ$",
      PGK: "K",
      SBD: "SI$",
      VUV: "Vt",
      WST: "WS$",
      TOP: "T$",
      NIO: "C$",
      GTQ: "Q",
      HNL: "L",
      SVC: "$",
      BZD: "BZ$",
      JMD: "J$",
      TTD: "TT$",
      BBD: "Bds$",
      XCD: "$",
      AWG: "ƒ",
      ANG: "ƒ",
      SRD: "$",
      GYD: "G$",
      VES: "Bs",
      COP: "$",
      PEN: "S/",
      BOB: "Bs",
      CLP: "$",
      ARS: "$",
      UYU: "$U",
      PYG: "₲",
      FKP: "£",
      GIP: "£",
      SHP: "£",
    };

    return currencyMap[currencyCode.toUpperCase()] || currencyCode;
  };

  // Format price with proper currency symbol and remove .00
  const formatPrice = (price: string | number, currency?: string) => {
    if (!price) return "";

    // Convert to number if it's a string
    const numericPrice = typeof price === "string" ? parseFloat(price) : price;

    // Handle NaN case
    if (isNaN(numericPrice)) return "";

    const currencySymbol = getCurrencySymbol(currency);

    // Format the number, removing .00 if it's a whole number
    const formattedNumber =
      numericPrice % 1 === 0 ? numericPrice.toString() : numericPrice.toFixed(2);

    return `${currencySymbol}${formattedNumber}`;
  };

  // Format price from cents (common in payment systems)
  const formatPriceFromCents = (priceInCents: number, currency?: string) => {
    if (!priceInCents) return "";

    const priceInMainUnit = priceInCents / 100;
    return formatPrice(priceInMainUnit, currency);
  };

  return {
    formatPrice,
    formatPriceFromCents,
    getCurrencySymbol,
  };
};
